import React, { useRef } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import InfiniteLoader from 'react-window-infinite-loader';

import { type UserOverviewDTO, userOverviewService } from 'entities/userOverview';
import { withSuspense } from 'shared/lib/react';
import { pathKeys } from 'shared/lib/react-router';
import { LinkButton } from 'shared/ui/LinkButton';
import { Loader } from 'shared/ui/Loader';
import { VirtualizedTableList } from 'shared/ui/VirtualTable';

import { columns, ROW_HEIGHT, THRESHOLD_VALUE, DEFAULT_FILTRATION, PAGE_SIZE } from './UserOverviewWidget.const';

import mapDataToTableColumns from './UserOverviewWidget.libs';
import styles from './UserOverviewWidget.module.scss';

const UserOverviewWidget: React.FC = () => {
  const { t } = useTranslation();

  const loaderRef = useRef<InfiniteLoader>(null);

  const {
    data: userListData,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery(userOverviewService.queryOptions({ filtration: DEFAULT_FILTRATION, size: PAGE_SIZE }));

  const mappedData = userListData?.pages?.reduce<UserOverviewDTO[]>((acc, { content }) => [...acc, ...content], []);

  const tableDataMapped = mapDataToTableColumns(mappedData);

  return (
    <div className={styles.rootWrapper}>
      <div className={styles.heading}>
        <Typography.Title level={5}>{t('UserOverview:UserOverviewWidget.heading')}</Typography.Title>
        <div>
          <LinkButton
            buttonProps={{ type: 'primary' }}
            linkProps={{ to: `${pathKeys.userOverview()}?availableUsers=true` }}
          >
            {t('UserOverview:UserOverviewWidget.toPanoramaButton.text')}
          </LinkButton>
        </div>
      </div>
      <div className={styles.listWrapper} data-testid="user-overview-widget">
        <VirtualizedTableList
          columns={columns}
          dataSource={tableDataMapped}
          rowHeight={ROW_HEIGHT}
          loadNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isNextPageLoading={isFetching}
          loaderRef={loaderRef}
          threshold={THRESHOLD_VALUE}
          bordered={false}
        />
      </div>
    </div>
  );
};

export default withSuspense(UserOverviewWidget, { fallback: <Loader /> });
